package top.wcpe.allincore.dsl.compiler.codegen.internal

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.ir.codec.IrByteWriter

/**
 * 语句序列化：负责把 [StmtAst] 写入字节流。
 *
 * 从 [ExprSerializer] 拆出，承接所有 STMT_* 写入逻辑（含 net.request lower 形态），
 * wire 格式定义参见 [ExprSerializer] 顶部注释。
 */
internal object StmtSerializer {

    // ---- Stmt 标签 ----
    private const val STMT_ASSIGN = 1
    private const val STMT_REQUEST = 2
    internal const val STMT_EXPR_STMT = 3
    private const val STMT_VAR_DECL = 4
    private const val STMT_IF = 5
    private const val STMT_WHILE = 6
    private const val STMT_BREAK = 7
    private const val STMT_CONTINUE = 8
    private const val STMT_RETURN = 9
    private const val STMT_TRY = 10

    // C2 新增语句 tag。
    private const val STMT_FOR_IN = 11
    private const val STMT_WHEN = 12

    // C4 新增语句 tag：局部 fun 声明。
    private const val STMT_LOCAL_FUN_DECL = 13

    // C5 新增语句 tag：net.request lower 后的请求语句，
    // wire format 与 STMT_REQUEST 完全一致，仅在解码时把 netMode 置 true。
    private const val STMT_NET_REQUEST = 14

    // C6 新增语句 tag：在 STMT_NET_REQUEST 载荷后追加 timeoutMs 表达式。
    private const val STMT_NET_REQUEST_TIMEOUT = 15

    /** v24：session.<name> = rhs 赋值。wire 格式：LP-UTF8 fieldName + Expr(rhs)。 */
    private const val STMT_SESSION_ASSIGN = 20

    /** v24：persist.<name> = rhs 赋值。wire 格式：LP-UTF8 fieldName + Expr(rhs)。 */
    private const val STMT_PERSIST_ASSIGN = 21

    /** v25：batch { stmts } 合批块。wire 格式：u16 stmtCount + { stmt }*。 */
    private const val STMT_BATCH = 22

    // ---- Callback 种类 ----
    private const val CALLBACK_PENDING = 0
    private const val CALLBACK_SUCCESS = 1
    private const val CALLBACK_FAIL = 2
    private const val CALLBACK_TIMEOUT = 3

    /** net.request 注入结果对象的统一参数名。 */
    private const val RESULT_PARAM: String = "__result"

    /**
     * 把 fun body / lambda body / action body 写入字节流：
     *  - 含 await：写 1 条 STATE_MACHINE_HEADER stmt（u16 stmtCount=1 + 1 个状态机）
     *  - 不含 await：原样写 N 条普通 stmt（向后兼容路径）
     */
    internal fun writeBodyAsStmtList(out: IrByteWriter, stmts: List<StmtAst>) {
        val lowered = try {
            StateMachineLowerer.lower(stmts)
        } catch (e: IllegalStateException) {
            throw IllegalStateException(e.message ?: "状态机 lower 失败", e)
        }
        if (!lowered.hasAwait) {
            require(stmts.size <= SerializerLimits.U16_MAX) { "stmt 数超过 65535" }
            out.writeShort(stmts.size)
            for (s in stmts) writeStmt(out, s)
            return
        }
        out.writeShort(1)
        // v16：状态机作为顶层 stmt 也需 position 前缀；取第一条 stmt 的位置作为整体近似位置
        val smPos = stmts.firstOrNull()?.position
        writePosition(out, smPos?.line ?: 0, smPos?.column ?: 0)
        StateMachineSerializer.writeStateMachine(out, lowered)
    }

    internal fun writeStmt(out: IrByteWriter, stmt: StmtAst) {
        // v16：写入源码位置（u16 line + u16 column）；钳位到 [0, 65535]，负值视为未知写 0
        writePosition(out, stmt.position.line, stmt.position.column)
        when (stmt) {
            is StmtAst.AssignStmt -> {
                when (val lhs = stmt.lhs) {
                    is LValueAst.StateLValue -> {
                        out.writeByte(STMT_ASSIGN)
                        out.writeByte(ExprSerializer.LV_STATE)
                        ExprSerializer.writeLpString(out, lhs.name)
                    }
                    is LValueAst.LocalLValue -> {
                        out.writeByte(STMT_ASSIGN)
                        out.writeByte(ExprSerializer.LV_LOCAL)
                        ExprSerializer.writeLpString(out, lhs.name)
                    }
                    is LValueAst.IndexLValue -> {
                        // M6-a：wire 格式 —— LV_INDEX + Expr(target) + Expr(index)
                        out.writeByte(STMT_ASSIGN)
                        out.writeByte(ExprSerializer.LV_INDEX)
                        ExprSerializer.writeExpr(out, lhs.target)
                        ExprSerializer.writeExpr(out, lhs.index)
                    }
                    is LValueAst.SessionLValue -> {
                        // session.x = rhs 走独立 tag，runtime 路由到 SharedStateRegistry。
                        out.writeByte(STMT_SESSION_ASSIGN)
                        ExprSerializer.writeLpString(out, lhs.name)
                        ExprSerializer.writeExpr(out, stmt.rhs)
                        return
                    }
                    is LValueAst.PersistLValue -> {
                        // persist.x = rhs 走独立 tag，runtime 路由到 PersistentStateStore。
                        out.writeByte(STMT_PERSIST_ASSIGN)
                        ExprSerializer.writeLpString(out, lhs.name)
                        ExprSerializer.writeExpr(out, stmt.rhs)
                        return
                    }
                }
                ExprSerializer.writeExpr(out, stmt.rhs)
            }
            // request 语句序列化（已废弃：StmtAst.RequestStmt 已删除；wire tag STMT_REQUEST=2 仅作 v3-v7 反序列化兼容保留）。
            is StmtAst.ExprStmt -> {
                // C5：ExprStmt 持有 NetRequestCall 时，lower 为 STMT_NET_REQUEST：
                // - 复用 STMT_REQUEST 的 wire format（namespace/handler/payload/4 callback）
                // - 4 个回调体共享同一个用户回调（lambda 内联 4 份 / FunRef 转 CallExpr）
                val call = stmt.call
                if (call is ExprAst.NetRequestCall) {
                    writeNetRequest(out, call)
                } else {
                    out.writeByte(STMT_EXPR_STMT)
                    ExprSerializer.writeExpr(out, call)
                }
            }
            is StmtAst.VarDeclStmt -> {
                out.writeByte(STMT_VAR_DECL)
                ExprSerializer.writeLpString(out, stmt.name)
                ExprSerializer.writeExpr(out, stmt.initializer)
            }
            is StmtAst.IfStmt -> {
                out.writeByte(STMT_IF)
                ExprSerializer.writeExpr(out, stmt.cond)
                writeStmtList(out, stmt.thenBody)
                writeStmtList(out, stmt.elseBody)
            }
            is StmtAst.WhileStmt -> {
                out.writeByte(STMT_WHILE)
                ExprSerializer.writeExpr(out, stmt.cond)
                writeStmtList(out, stmt.body)
            }
            is StmtAst.BreakStmt -> out.writeByte(STMT_BREAK)
            is StmtAst.ContinueStmt -> out.writeByte(STMT_CONTINUE)
            is StmtAst.ReturnStmt -> {
                out.writeByte(STMT_RETURN)
                if (stmt.value == null) {
                    out.writeByte(0)
                } else {
                    out.writeByte(1)
                    ExprSerializer.writeExpr(out, stmt.value)
                }
            }
            is StmtAst.TryStmt -> {
                out.writeByte(STMT_TRY)
                writeStmtList(out, stmt.tryBody)
                ExprSerializer.writeLpString(out, stmt.catchName)
                writeStmtList(out, stmt.catchBody)
                writeStmtList(out, stmt.finallyBody)
            }
            is StmtAst.ForInStmt -> {
                out.writeByte(STMT_FOR_IN)
                ExprSerializer.writeLpString(out, stmt.varName)
                ExprSerializer.writeExpr(out, stmt.iterable)
                writeStmtList(out, stmt.body)
            }
            is StmtAst.WhenStmt -> {
                out.writeByte(STMT_WHEN)
                ExprSerializer.writeWhenPayload(out, stmt.whenExpr)
            }
            is StmtAst.FunDeclStmt -> {
                out.writeByte(STMT_LOCAL_FUN_DECL)
                val decl = stmt.decl
                ExprSerializer.writeLpString(out, decl.name)
                require(decl.params.size <= SerializerLimits.U16_MAX) { "局部 fun 参数数超过 65535" }
                out.writeShort(decl.params.size)
                // M5-b：每个形参写入 LP name + u8 hasDefault + Expr?
                val localDefaults = decl.defaults
                for (i in decl.params.indices) {
                    ExprSerializer.writeLpString(out, decl.params[i])
                    val def = localDefaults.getOrNull(i)
                    if (def == null) {
                        out.writeByte(0)
                    } else {
                        out.writeByte(1)
                        ExprSerializer.writeExpr(out, def)
                    }
                }
                // D2-b：局部 fun 体独立走状态机 lowerer；含 await 时输出 1 条 StateMachine stmt
                writeBodyAsStmtList(out, decl.body)
            }
            is StmtAst.BatchStmt -> {
                out.writeByte(STMT_BATCH)
                writeStmtList(out, stmt.body)
            }
        }
    }

    /** 写一段语句列表：u16 count + count 条 stmt。 */
    internal fun writeStmtList(out: IrByteWriter, stmts: List<StmtAst>) {
        require(stmts.size <= SerializerLimits.U16_MAX) { "语句数超过 65535" }
        out.writeShort(stmts.size)
        for (s in stmts) writeStmt(out, s)
    }

    /**
     * 把 [ExprAst.NetRequestCall] lower 为 STMT_NET_REQUEST，wire format 复用
     * STMT_REQUEST：namespace / handler / payload / 4 个 Callback。
     *
     * - 用户回调（lambda 或 FunRef）作为单条 ExprStmt 嵌入到 4 份回调 body 中
     * - paramName 统一为 "__result"；回调体形如 `__cb(__result)` 将合成的 result 注入
     * - runtime 触发时，由 RequestCallbacks 把 `{kind, payload}` 写到 locals["__result"]
     */
    private fun writeNetRequest(out: IrByteWriter, net: ExprAst.NetRequestCall) {
        val callback = net.callback
            ?: error("net.request 缺少 callback —— 仅 await 形态允许省略 callback（pos=${net.position}）")
        out.writeByte(if (net.timeoutMs == null) STMT_NET_REQUEST else STMT_NET_REQUEST_TIMEOUT)
        ExprSerializer.writeLpString(out, net.namespace)
        ExprSerializer.writeLpString(out, net.handler)

        require(net.payload.size <= SerializerLimits.U16_MAX) { "net.request payload 项数超过 65535" }
        out.writeShort(net.payload.size)
        for (entry in net.payload) {
            ExprSerializer.writeLpString(out, entry.key)
            ExprSerializer.writeExpr(out, entry.value)
        }

        // 4 个回调（顺序固定 PENDING/SUCCESS/FAIL/TIMEOUT），paramName 统一 "__result"
        val kinds = listOf(
            CALLBACK_PENDING, CALLBACK_SUCCESS, CALLBACK_FAIL, CALLBACK_TIMEOUT,
        )
        out.writeByte(kinds.size)
        for (k in kinds) {
            writeNetCallbackBody(out, callback, k)
        }
        net.timeoutMs?.let { ExprSerializer.writeExpr(out, it) }
    }

    /**
     * 写入单个 net.request 回调（{kind, paramName, body} 三件套）。
     * body 固定为单条 ExprStmt(CallExpr(callback, [IdentRef("__result")]))。
     */
    private fun writeNetCallbackBody(out: IrByteWriter, callback: ExprAst, kind: Int) {
        out.writeByte(kind)
        ExprSerializer.writeLpString(out, RESULT_PARAM)
        out.writeShort(1)
        // v16：合成回调语句无源码位置，写入 0,0
        writePosition(out, 0, 0)
        out.writeByte(STMT_EXPR_STMT)
        // CallExpr tag
        out.writeByte(ExprSerializer.TAG_CALL_EXPR)
        ExprSerializer.writeExpr(out, callback)
        // v11 M5-b：CallExpr 头部新增 u8 hasNamedArgs（此处合成调用，无命名实参，固定 0）
        out.writeByte(0)
        out.writeShort(1) // 1 个参数
        // IdentRef("__result")
        out.writeByte(ExprSerializer.TAG_IDENT_REF)
        ExprSerializer.writeLpString(out, RESULT_PARAM)
    }

    /**
     * 写入 position 前缀（u16 line + u16 column）。
     * 负值钳位到 0（表示未知），超过 65535 钳位到 65535。
     */
    private fun writePosition(out: IrByteWriter, line: Int, column: Int) {
        out.writeShort(line.coerceIn(0, SerializerLimits.U16_MAX))
        out.writeShort(column.coerceIn(0, SerializerLimits.U16_MAX))
    }
}
